import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import showToast from "../components/Toast";
import { authHeader } from "../session";

// Tournaments are still the simple listing model; phase P4 replaces them with
// categories, entries and draws. This screen exists so the calendar stays
// manageable in the meantime — keep it thin.

const errorMessage = (error, fallback) =>
  error.response?.data?.error || fallback;

export const getTournaments = async () => {
  // The endpoint paginates at 10 by default. An administrator wants the whole
  // calendar in one list, so ask for it.
  const response = await axios.get("/api/v1/public/tournaments", {
    params: { limit: 200 },
  });
  return response.data.data;
};

export const useTournaments = () =>
  useQuery("tournaments", getTournaments, { refetchOnWindowFocus: false });

export const createTournament = async (tournament) => {
  const response = await axios.post("/api/v1/admin/tournaments", tournament, {
    headers: authHeader(),
  });
  return response.data.data;
};

export const useCreateTournament = () => {
  const queryClient = useQueryClient();

  return useMutation(createTournament, {
    onSuccess: (tournament) => {
      queryClient.invalidateQueries("tournaments");
      showToast(`Added ${tournament.name}.`);
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't add that tournament."), "error"),
  });
};

export const updateTournament = async ({ id, ...tournament }) => {
  const response = await axios.put(
    `/api/v1/admin/tournaments/${id}`,
    tournament,
    { headers: authHeader() }
  );
  return response.data.data;
};

export const useUpdateTournament = () => {
  const queryClient = useQueryClient();

  return useMutation(updateTournament, {
    onSuccess: (tournament) => {
      queryClient.invalidateQueries("tournaments");
      showToast(`Saved changes to ${tournament.name}.`);
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't save those changes."), "error"),
  });
};

export const deleteTournament = async (id) => {
  const response = await axios.delete(`/api/v1/admin/tournaments/${id}`, {
    headers: authHeader(),
  });
  return response.data;
};

export const useDeleteTournament = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteTournament, {
    onSuccess: () => {
      queryClient.invalidateQueries("tournaments");
      showToast("Tournament deleted.");
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't delete that tournament."), "error"),
  });
};

// The tournament hero image. Uploaded in its own request rather than inline
// with the form because it is multipart: the API resizes it through sharp and
// pushes it to Cloudinary, then returns the saved tournament.
export const uploadTournamentPhoto = async ({ id, file }) => {
  const form = new FormData();
  form.append("image", file);

  const response = await axios.put(
    `/api/v1/admin/tournaments/${id}/update-tournament-image`,
    form,
    { headers: authHeader() }
  );
  return response.data.data;
};

export const useUploadTournamentPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation(uploadTournamentPhoto, {
    onSuccess: () => {
      queryClient.invalidateQueries("tournaments");
      showToast("Tournament photo updated.");
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't upload that photo."), "error"),
  });
};
